import { z } from 'zod';

const STORAGE_KEY = 'phoebe.teams.connection';
const connectionSchema = z.object({
    installationId: z.string().uuid(),
    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    capturedAt: z.number(),
});
let inMemoryConnection: z.infer<typeof connectionSchema> | null = null;

// Run before router creation and telemetry initialization. A fragment keeps
// the one-use credential out of HTTP URLs; session storage survives sign-in.
export function captureTeamsConnection() {
    if (!window.location.hash.startsWith('#teamsConnect=')) {
        return;
    }
    const value = new URLSearchParams(window.location.hash.slice(1)).get(
        'teamsConnect',
    );
    window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search,
    );
    clearTeamsConnection();
    const [installationId, token] = (value ?? '').split('.');
    const result = connectionSchema.safeParse({
        installationId,
        token,
        capturedAt: Date.now(),
    });
    if (result.success) {
        inMemoryConnection = result.data;
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(result.data));
        } catch {
            // Storage may be unavailable; a signed-in user can still connect.
        }
    }
}

export function readTeamsConnection() {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
            const result = connectionSchema.safeParse(JSON.parse(stored));
            inMemoryConnection = result.success ? result.data : null;
        }
    } catch {
        // Invalid or obsolete local state must not prevent opening settings.
    }
    if (
        inMemoryConnection &&
        Date.now() - inMemoryConnection.capturedAt < 30 * 60 * 1000
    ) {
        return inMemoryConnection;
    }
    clearTeamsConnection();
    return null;
}

export function clearTeamsConnection() {
    inMemoryConnection = null;
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // No persistent state is available in this browser.
    }
}
